import { createSlice, PayloadAction } from '@reduxjs/toolkit';

const defaultMessages = [
  'Loading data',
  'Checking permissions',
  'Preparing interface',
  'Almost ready...',
];

const logoutMessages = [
  'Clearing permissions...',
  'Resetting session...',
  'Logging out...',
  'See you soon!',
];

interface LoadingOverlayState {
  isOpen: boolean;
  message: string;
  messages: string[];
  forceShow: boolean;
  mode: 'default' | 'logout';
  progress: number; // 0-100
  minVisibleUntil: number | null;
}

const initialState: LoadingOverlayState = {
  isOpen: false,
  message: 'Loading...',
  messages: defaultMessages,
  forceShow: false,
  mode: 'default',
  progress: 0,
  minVisibleUntil: null,
};

const loadingOverlaySlice = createSlice({
  name: 'loadingOverlay',
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      const open = action.payload;
      if (open && !state.isOpen) {
        // When opening, set the minimum display time
        state.isOpen = open;
        state.minVisibleUntil = Date.now() + 2000; // 2 seconds
      } else if (!open && state.isOpen) {
        // When closing, just update the state
        // Component will handle the minimum display time check
        state.isOpen = false;
        state.minVisibleUntil = null;
      }
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    setMessages: (state, action: PayloadAction<string[]>) => {
      state.messages = action.payload;
    },
    setForceShow: (state, action: PayloadAction<boolean>) => {
      state.forceShow = action.payload;
    },
    setMode: (state, action: PayloadAction<'default' | 'logout'>) => {
      const mode = action.payload;
      state.mode = mode;
      state.messages = mode === 'logout' ? logoutMessages : defaultMessages;
    },
    setProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setMinVisibleUntil: (state, action: PayloadAction<number | null>) => {
      state.minVisibleUntil = action.payload;
    },
    reset: state => {
      state.isOpen = false;
      state.mode = 'default';
      state.messages = defaultMessages;
      state.minVisibleUntil = null;
      state.forceShow = false;
    },
  },
});

export const {
  setOpen,
  setMessage,
  setMessages,
  setForceShow,
  setMode,
  setProgress,
  setMinVisibleUntil,
  reset,
} = loadingOverlaySlice.actions;
export default loadingOverlaySlice.reducer;

// Selectors
import type { RootState } from '../index';

export const selectIsOpen = (state: RootState) => state.loadingOverlay.isOpen;
export const selectMessage = (state: RootState) =>
  state.loadingOverlay.message;
export const selectForceShow = (state: RootState) =>
  state.loadingOverlay.forceShow;
export const selectMode = (state: RootState) => state.loadingOverlay.mode;
export const selectProgress = (state: RootState) =>
  state.loadingOverlay.progress;
export const selectMessages = (state: RootState) =>
  state.loadingOverlay.messages;
export const selectMinVisibleUntil = (state: RootState) =>
  state.loadingOverlay.minVisibleUntil;

